import { type MetaFunction } from '@remix-run/node'
import { type ReactNode } from 'react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'

// NOTE: Drafted in-house. Have counsel review before public launch.

const SECTIONS = [
	{ id: 'overview', title: 'Overview' },
	{ id: 'collection', title: 'Information we collect' },
	{ id: 'use', title: 'How we use information' },
	{ id: 'sharing', title: 'Sharing and disclosure' },
	{ id: 'retention', title: 'Data retention' },
	{ id: 'security', title: 'Security' },
	{ id: 'rights', title: 'Your rights' },
	{ id: 'contact', title: 'Contact' },
]

export const meta: MetaFunction = () => [
	{ title: 'Privacy — Momentum' },
	{ name: 'description', content: 'Privacy policy for Momentum.' },
]

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-relaxed text-ink-fg-soft">
			{children}
		</p>
	)
}

function Ul({ children }: { children: ReactNode }) {
	return (
		<ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-ink-fg-soft">
			{children}
		</ul>
	)
}

export default function PrivacyRoute() {
	return (
		<>
			<MarketingHero
				eyebrow="Privacy"
				heading="Privacy"
				subhead="Last updated: July 7, 2026"
				ctaLabel=""
				variant="compact"
			/>

			<section className="mx-auto w-full max-w-5xl px-6 py-16 md:px-10 md:py-20">
				<div className="grid gap-12 md:grid-cols-[14rem_1fr]">
					<aside className="md:sticky md:top-24 md:self-start">
						<EyebrowLabel>Contents</EyebrowLabel>
						<ul className="mt-6 space-y-2 text-sm">
							{SECTIONS.map((s) => (
								<li key={s.id}>
									<a
										href={`#${s.id}`}
										className="text-ink-fg-soft hover:text-ink-fg"
									>
										{s.title}
									</a>
								</li>
							))}
						</ul>
					</aside>

					<article className="max-w-[60ch]">
						<section id="overview" className="mb-12">
							<h2 className="font-display text-display-3">Overview</h2>
							<P>
								This Privacy Policy explains what information Momentum
								(“Momentum,” “we,” or “us”) collects when you use our website,
								applications, and services (together, the “Service”), how we use
								it, and the choices you have. It is part of our{' '}
								<a href="/tos" className="underline hover:text-ink-fg">
									Terms of Service
								</a>
								.
							</P>
							<P>
								The short version: we collect the information needed to run a
								training log — your account details, your fitness profile, and
								the workouts you record. We use it to provide the Service. We do
								not sell your data or share it with non-affiliated parties, we
								do not show third-party advertising, and we delete your data
								upon request.
							</P>
							<P>
								We may update this policy from time to time. If a change is
								material, we will notify you — for example by email or by a
								notice in the Service — before it takes effect. The “Last
								updated” date above reflects the current version.
							</P>
						</section>

						<section id="collection" className="mb-12">
							<h2 className="font-display text-display-3">
								Information we collect
							</h2>
							<P>
								<strong>Account information.</strong> When you sign up we
								collect your email address, username, and name, and a password,
								which we store only in hashed form. If you sign in with a
								third-party provider such as GitHub or Google, that provider
								shares basic profile information with us, such as your name,
								email address, and profile photo. If you enable two-factor
								authentication, we store the data needed to verify your codes.
							</P>
							<P>
								<strong>Fitness and training data.</strong> The Service is built
								around the information you record: fitness profiles and goals,
								workouts and exercise logs, performance metrics, notes, and any
								photos you upload. You control what you enter — record only what
								you are comfortable storing.
							</P>
							<P>
								<strong>Technical data.</strong> Like most services, we
								automatically collect log and device information when you use
								the Service, such as IP address, browser type, pages visited,
								and performance telemetry. We use cookies and similar
								technologies only for essential purposes such as keeping you
								signed in — not for advertising or cross-site tracking.
							</P>
						</section>

						<section id="use" className="mb-12">
							<h2 className="font-display text-display-3">
								How we use information
							</h2>
							<P>We use the information we collect to:</P>
							<Ul>
								<li>
									provide and operate the Service — recording workouts,
									computing analytics and achievements, and supporting coaching
									relationships and groups;
								</li>
								<li>
									power optional AI features, such as generating workout
									suggestions from your training history;
								</li>
								<li>
									keep the Service secure and working — authentication,
									debugging, performance monitoring, and abuse prevention;
								</li>
								<li>
									communicate with you about the Service, such as email
									verification, password resets, and notices about changes; and
								</li>
								<li>comply with legal obligations.</li>
							</Ul>
							<P>
								We do not sell your personal information or share it with
								non-affiliated parties, and we do not use it for third-party
								advertising.
							</P>
						</section>

						<section id="sharing" className="mb-12">
							<h2 className="font-display text-display-3">
								Sharing and disclosure
							</h2>
							<P>
								<strong>People you choose.</strong> Your training data is
								visible to people you share it with through the Service — for
								example, coaches you work with and groups you join. You control
								those relationships and can end them at any time.
							</P>
							<P>
								<strong>Service providers.</strong> We use third-party providers
								to run the Service, such as hosting infrastructure, email
								delivery, and AI model providers that process training data to
								generate workout suggestions. These providers process your
								information on our behalf and under our instructions; they are
								not permitted to use it for their own purposes.
							</P>
							<P>
								<strong>Legal and safety.</strong> We may disclose information
								if required by law, or if we believe in good faith it is
								necessary to protect the rights, safety, or property of our
								members, the public, or Momentum. If Momentum is involved in a
								merger, acquisition, or sale of assets, your information may be
								transferred as part of that transaction, and we will notify you
								before your information becomes subject to a different privacy
								policy.
							</P>
						</section>

						<section id="retention" className="mb-12">
							<h2 className="font-display text-display-3">Data retention</h2>
							<P>
								We keep your information for as long as your account is active.
								If you delete your account, delete individual content, or ask us
								to delete your data, we delete or de-identify the associated
								information within a reasonable time, and it is purged from
								backups on our regular backup rotation schedule. We may retain
								some records longer where required by law or needed to resolve
								disputes, and we may keep aggregated, de-identified data that no
								longer identifies you.
							</P>
						</section>

						<section id="security" className="mb-12">
							<h2 className="font-display text-display-3">Security</h2>
							<P>
								We protect your information with measures including encryption
								in transit, hashed password storage, optional two-factor
								authentication, and permission-based access controls that limit
								who can see your data. No method of transmission or storage is
								completely secure, so we cannot guarantee absolute security — if
								we learn of a breach affecting your personal information, we
								will notify you as required by law.
							</P>
						</section>

						<section id="rights" className="mb-12">
							<h2 className="font-display text-display-3">Your rights</h2>
							<P>
								You can view and update your profile and training data, and
								delete individual content or your entire account, from your
								account settings at any time. You can also ask us to delete your
								data, and we will honor the request regardless of where you
								live. Depending on your location, you may additionally have
								statutory rights to access, correct, or receive a copy of your
								personal information, or to object to or restrict certain
								processing.
							</P>
							<P>
								To exercise any of these rights, use your account settings or
								contact us at{' '}
								<a
									href="mailto:john@momentum.fitness"
									className="underline hover:text-ink-fg"
								>
									john@momentum.fitness
								</a>
								. We will not discriminate against you for exercising your
								privacy rights.
							</P>
						</section>

						<section id="contact" className="mb-12">
							<h2 className="font-display text-display-3">Contact</h2>
							<P>
								Questions about this policy or your data? Contact us at{' '}
								<a
									href="mailto:john@momentum.fitness"
									className="underline hover:text-ink-fg"
								>
									john@momentum.fitness
								</a>
								.
							</P>
							<P>
								Momentum is operated from the United States, and this policy is
								governed by the laws of the State of Indiana. If you use the
								Service from elsewhere, you understand that your information is
								processed in the United States.
							</P>
						</section>
					</article>
				</div>
			</section>
		</>
	)
}
